// Route chunks use lazyWithRetry (not React.lazy) so a stale-chunk fetch after a
// deploy retries silently and only reloads as a last resort — see utils/lazyWithRetry.
import { lazyWithRetry } from '../utils/lazyWithRetry';

// Admin Panel
export const AdminLogin = lazyWithRetry(() => import('../pages/admin/Login').then(m => ({ default: m.AdminLogin })));
export const AdminAuthCallback = lazyWithRetry(() => import('../pages/admin/AuthCallback').then(m => ({ default: m.AdminAuthCallback })));
export const AdminDashboard = lazyWithRetry(() => import('../pages/admin/Dashboard'));
export const AdminTenants = lazyWithRetry(() => import('../pages/admin/Tenants'));
export const AdminCalendar = lazyWithRetry(() => import('../pages/admin/Calendar'));
export const AdminSupport = lazyWithRetry(() => import('../pages/admin/Support'));
export const AdminLogs = lazyWithRetry(() => import('../pages/admin/Logs'));
export const AdminHealth = lazyWithRetry(() => import('../pages/admin/Health'));
export const AdminRevenue = lazyWithRetry(() => import('../pages/admin/Revenue'));
export const AdminUsers = lazyWithRetry(() => import('../pages/admin/Users'));
export const AdminCredits = lazyWithRetry(() => import('../pages/admin/Credits'));
export const AdminMonitoring = lazyWithRetry(() => import('../pages/admin/Monitoring'));
export const AdminMarketing = lazyWithRetry(() => import('../pages/admin/Marketing'));
// New super-admin pages — merchant-parity surfaces
export const AdminEmailMarketing = lazyWithRetry(() => import('../pages/admin/EmailMarketing'));
export const AdminPopups = lazyWithRetry(() => import('../pages/admin/Popups'));
export const AdminSocialAutomator = lazyWithRetry(() => import('../pages/admin/SocialAutomator'));
export const AdminBusinessHub = lazyWithRetry(() => import('../pages/admin/BusinessHub'));
export const AdminBrandDNA = lazyWithRetry(() => import('../pages/admin/BrandDNA'));
export const AdminIntegrations = lazyWithRetry(() => import('../pages/admin/Integrations'));
export const AdminTeam = lazyWithRetry(() => import('../pages/admin/Team'));
export const AdminApiKeys = lazyWithRetry(() => import('../pages/admin/ApiKeys'));

// Shopify Embedded App — disabled (standalone app, not embedded)
export const ShopifySettings = lazyWithRetry(() => import('../pages/settings/ShopifySettings'));

// Lazy load heavy routes for bundle optimization
// /inbox renders the rich 3-panel Unified Inbox (list + thread + customer context),
// promoted from the old Agent "Unified Inbox" tab.
export const Inbox = lazyWithRetry(() => import('../pages/inbox/UnifiedInboxPage'));
export const Popups = lazyWithRetry(() => import('../pages/Popups').then(m => ({ default: m.Popups })));
export const Settings = lazyWithRetry(() => import('../pages/Settings').then(m => ({ default: m.Settings })));
export const Onboarding = lazyWithRetry(() => import('../pages/Onboarding'));
// Signup is eagerly imported in App.tsx (same as Login) so /signup never
// waits on a Suspense chunk — tunnels and cold CDN edges made that feel stuck.
export const BuzzTalk = lazyWithRetry(() => import('../pages/buzztalk/BuzzTalk'));

export const BusinessHub = lazyWithRetry(() => import('../pages/business/BusinessHub'));

export const Suppliers = lazyWithRetry(() => import('../pages/ops/Suppliers'));
export const Customers = lazyWithRetry(() => import('../pages/business/Customers'));
export const Orders = lazyWithRetry(() => import('../pages/business/Orders'));
export const Products = lazyWithRetry(() => import('../pages/business/Products'));

export const TikTokSettings = lazyWithRetry(() => import('../pages/settings/TikTokSettings'));
// YouTube, X and LinkedIn share ONE screen (SocialChannelSettings) — the named exports
// are thin wrappers so the router still reads one route, one component.
export const YouTubeSettings = lazyWithRetry(() =>
  import('../pages/settings/SocialChannelSettings').then((m) => ({ default: m.YouTubeSettings })),
);
export const XSettings = lazyWithRetry(() =>
  import('../pages/settings/SocialChannelSettings').then((m) => ({ default: m.XSettings })),
);
export const LinkedInSettings = lazyWithRetry(() =>
  import('../pages/settings/SocialChannelSettings').then((m) => ({ default: m.LinkedInSettings })),
);
export const HubSpotSettings = lazyWithRetry(() => import('../pages/settings/HubSpotSettings'));
export const SalesforceSettings = lazyWithRetry(() => import('../pages/settings/SalesforceSettings'));
export const GoogleSheetsSettings = lazyWithRetry(() => import('../pages/settings/GoogleSheetsSettings'));
export const GoogleAdsSettings = lazyWithRetry(() => import('../pages/settings/GoogleAdsSettings'));
export const GmailSettings = lazyWithRetry(() => import('../pages/settings/GmailSettings'));
export const TikTokAdsSettings = lazyWithRetry(() => import('../pages/settings/TikTokAdsSettings'));
export const Team = lazyWithRetry(() => import('../pages/settings/Team'));
export const Brand = lazyWithRetry(() => import('../pages/settings/Brand'));
export const Billing = lazyWithRetry(() => import('../pages/settings/Billing'));
export const EmailDomainSettings = lazyWithRetry(() => import('../pages/settings/EmailDomainSettings'));
export const ProductAssets = lazyWithRetry(() => import('../pages/settings/ProductAssets'));
export const VendorDashboard = lazyWithRetry(() => import('../pages/vendor/Dashboard'));
export const AgentCanvas = lazyWithRetry(() => import('../pages/agent/AgentCanvas').then(m => ({ default: m.AgentCanvas })));
export const Automations = lazyWithRetry(() => import('../pages/automations/Automations'));
export const AutomationsLab = lazyWithRetry(() => import('../pages/automations/lab/AutomationsLab'));

// FlowBuilder removed — routes redirect to /automations
export const SocialAutomator = lazyWithRetry(() => import('../pages/marketing/SocialAutomator'));
export const EmailMarketing = lazyWithRetry(() => import('../pages/email-marketing/EmailMarketing'));
export const FlowCanvas = lazyWithRetry(() => import('../pages/email-marketing/FlowCanvas'));
export const AutomationDetail = lazyWithRetry(() => import('../pages/email-marketing/AutomationDetail'));
export const CustomDomain = lazyWithRetry(() => import('../pages/settings/CustomDomain'));
export const KnowledgeBase = lazyWithRetry(() => import('../pages/KnowledgeBase'));
export const PrivacyCenter = lazyWithRetry(() => import('../pages/PrivacyCenter'));
export const WhiteLabel = lazyWithRetry(() => import('../pages/settings/WhiteLabel'));
export const CallPage = lazyWithRetry(() => import('../pages/call/CallPage')); // BuzzCall (W2) — public guest/host call page
export const AcceptInvite = lazyWithRetry(() => import('../pages/invite/AcceptInvite')); // Team invite accept — the emailed /invite/:token page
export const AcceptChatInvite = lazyWithRetry(() => import('../pages/chat/AcceptChatInvite')); // BuzzTalk guest accept — the emailed /chat/accept/:token page
